import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';

import { Portal } from '../lib/Portal';

import styles from './Toaster.module.css';

export type ToastTone = 'gold' | 'teal' | 'danger';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

export interface ToastApi {
  show(message: string, tone?: ToastTone): void;
}

const TOAST_TTL = 3200;
const MAX_VISIBLE = 3;

/**
 * Тосты живут вне React-дерева: их вызывают из onError мутаций, где хука
 * может не быть в области видимости. Простой pub/sub вместо контекста —
 * `useToast()` возвращает стабильный объект без ререндера потребителя.
 */
let counter = 0;
const listeners = new Set<(toasts: Toast[]) => void>();
let queue: Toast[] = [];

function emit(): void {
  for (const listener of listeners) listener(queue);
}

function pushToast(message: string, tone: ToastTone = 'teal'): void {
  counter += 1;
  const toast: Toast = { id: counter, message, tone };
  queue = [...queue, toast].slice(-MAX_VISIBLE);
  emit();
  window.setTimeout(() => {
    queue = queue.filter((item) => item.id !== toast.id);
    emit();
  }, TOAST_TTL);
}

function dismissToast(id: number): void {
  queue = queue.filter((item) => item.id !== id);
  emit();
}

/** Императивный вызов из не-React кода (обработчики ошибок клиента). */
export const toast: ToastApi = { show: pushToast };

export function useToast(): ToastApi {
  const ref = useRef<ToastApi>({ show: pushToast });
  return ref.current;
}

export function Toaster(): React.ReactElement {
  const [toasts, setToasts] = useState<Toast[]>(queue);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const listener = (next: Toast[]): void => setToasts(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const dismiss = useCallback((id: number) => dismissToast(id), []);

  return (
    <Portal>
      <div className={styles.region} role="region" aria-live="polite" aria-atomic="false">
        <AnimatePresence initial={false}>
          {toasts.map((item) => (
            <motion.button
              key={item.id}
              type="button"
              className={cn(styles.toast, styles[item.tone])}
              initial={{ opacity: 0, y: -12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => dismiss(item.id)}
            >
              {item.message}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    </Portal>
  );
}
